import React, {useEffect, useMemo, useState} from 'react'
import {Dropdown} from 'react-bootstrap'
import Select from 'react-select'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import {Form, Formik} from 'formik'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useRoles} from '../../../../hooks/useGlobalData'
import {
  AIInterpretationFilterModel,
  EMPTY_AI_INTERPRETATION_FILTER,
} from './types'

const STATUS_OPTIONS = [
  {value: 'ACTIVE', label: 'Active'},
  {value: 'DESABLE', label: 'Inactive'},
  {value: 'PENDING', label: 'Pending'},
]

const selectStyles = {
  control: (provided: any) => ({
    ...provided,
    width: '100%',
    maxHeight: '300px',
    overflowY: 'auto',
    border: '1px solid #e4e6ef',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
}

/** Prevents Bootstrap Dropdown from closing when interacting with react-select. */
function SelectField({children}: {children: React.ReactNode}) {
  return (
    <div onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  )
}

type Props = {
  value: AIInterpretationFilterModel
  onApply: (filter: AIInterpretationFilterModel) => void
}

export function AIInterpretationFilterDropdown({value, onApply}: Props) {
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const {data: rolesData, isLoading: isFetchingRole} = useRoles('CONSUMER')

  const roleOptions = useMemo(
    () => (rolesData ?? []).map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})),
    [rolesData]
  )

  useEffect(() => {
    setIsFilterApplied(
      value.status.length > 0 ||
        value.roles.length > 0 ||
        !!value.joinDate ||
        !!value.loginDate
    )
  }, [value])

  const handleReset = () => {
    setShowDropdownMenu(false)
    setIsFilterApplied(false)
    onApply({...EMPTY_AI_INTERPRETATION_FILTER})
  }

  return (
    <Dropdown show={showDropdownMenu} onToggle={setShowDropdownMenu} autoClose={false}>
      <OverlayTrigger placement='top' overlay={<Tooltip id='ai-interp-filter-tooltip'>Filter</Tooltip>}>
        <Dropdown.Toggle
          variant='primary'
          id='ai-interp-filter-toggle'
          className={`btn btn-icon btn-sm no-caret ${isFilterApplied ? 'btn-danger' : ''}`}
        >
          <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-muted' />
        </Dropdown.Toggle>
      </OverlayTrigger>
      <Dropdown.Menu
        className='p-4 max-w-300px w-300px'
        style={{minHeight: '100%', overflow: 'visible'}}
      >
          <Formik
            enableReinitialize
            initialValues={value}
            onSubmit={(values, {setSubmitting}) => {
              onApply(values)
              setShowDropdownMenu(false)
              setIsFilterApplied(true)
              setSubmitting(false)
            }}
          >
            {({handleSubmit, setFieldValue, values, isSubmitting, isValid, dirty}) => (
              <Form>
                <div className='px-4 pb-4'>
                  <div className='fs-4 text-dark fw-semibolder'>Filter</div>
                </div>
                <div className='separator border-gray-200' />
                <div className='px-4 mt-4'>
                  <div className='row flex-column'>
                    <div className='col-sm-12 col-md-12'>
                      <div className='mb-3'>
                        <div className='col-md-12'>
                          <div className='d-flex justify-content-between align-items-center'>
                            <label htmlFor='status' className='form-label fs-7 mb-0'>
                              Status
                            </label>
                            <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={
                                  STATUS_OPTIONS.filter(
                                    (option) =>
                                      Array.isArray(values.status) &&
                                      values.status.includes(option.value)
                                  ).length === STATUS_OPTIONS.length
                                }
                                onChange={(e) => {
                                  setFieldValue(
                                    'status',
                                    e.target.checked
                                      ? STATUS_OPTIONS.map((option) => option.value)
                                      : []
                                  )
                                }}
                              />
                              <label className='form-check-label'>All</label>
                            </div>
                          </div>
                        </div>
                        <div className='row g-4 g-xl-4'>
                          <div className='col-sm-12 col-md-12 col-lg-12'>
                            <SelectField>
                              <Select
                                isMulti
                                closeMenuOnSelect={false}
                                className='react-select-styled react-select-solid react-select-sm'
                                classNamePrefix='react-select'
                                options={STATUS_OPTIONS}
                                value={STATUS_OPTIONS.filter(
                                  (option) =>
                                    Array.isArray(values.status) &&
                                    values.status.includes(option.value)
                                )}
                                onChange={(selectedOptions: any[]) => {
                                  setFieldValue(
                                    'status',
                                    selectedOptions
                                      ? selectedOptions.map((option) => option.value)
                                      : []
                                  )
                                }}
                                placeholder='Select Status'
                                styles={selectStyles}
                              />
                            </SelectField>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='mb-3'>
                      <div className='col-md-12'>
                        <div className='d-flex justify-content-between align-items-center'>
                          <label htmlFor='role' className='form-label fs-7 mb-1'>
                            Role(s)
                          </label>
                          <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={values.roles?.length === roleOptions?.length && roleOptions.length > 0}
                              onChange={(e) => {
                                setFieldValue(
                                  'roles',
                                  e.target.checked
                                    ? roleOptions.map((option: {value: string}) => option.value)
                                    : []
                                )
                              }}
                            />
                            <label className='form-check-label'>All</label>
                          </div>
                        </div>
                      </div>
                      <div className='row g-4 g-xl-4'>
                        <div className='col-sm-12 col-md-12 col-lg-12'>
                          <SelectField>
                            <Select
                              className='react-select-styled react-select-solid react-select-sm'
                              classNamePrefix='react-select'
                              options={!isFetchingRole ? roleOptions : []}
                              placeholder={isFetchingRole ? 'Loading roles…' : 'Select Role(s)'}
                              isMulti
                              closeMenuOnSelect={false}
                              isLoading={isFetchingRole}
                              value={
                                roleOptions?.filter(
                                  (option: any) =>
                                    Array.isArray(values.roles) &&
                                    values.roles.includes(option.value)
                                ) || []
                              }
                              onChange={(selectedOptions: any[]) => {
                                setFieldValue(
                                  'roles',
                                  selectedOptions
                                    ? selectedOptions.map((option) => option.value)
                                    : []
                                )
                              }}
                              styles={selectStyles}
                            />
                          </SelectField>
                        </div>
                      </div>
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='joinDate' className='form-label fs-7 mb-1'>
                        Joined Date
                      </label>
                      <input
                        type='date'
                        className='form-control form-control-solid'
                        id='joinDate'
                        value={values.joinDate ?? ''}
                        onChange={(e) => setFieldValue('joinDate', e.target.value || null)}
                      />
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='loginDate' className='form-label fs-7 mb-1'>
                        Login Date
                      </label>
                      <input
                        type='date'
                        className='form-control form-control-solid'
                        id='loginDate'
                        value={values.loginDate ?? ''}
                        onChange={(e) => setFieldValue('loginDate', e.target.value || null)}
                      />
                    </div>
                  </div>

                  <div className='text-end mt-3 px-4'>
                    <button type='button' className='btn btn-sm btn-light me-2' onClick={handleReset}>
                      Reset
                    </button>
                    <button
                      type='button'
                      onClick={() => handleSubmit()}
                      className='btn btn-sm btn-primary'
                      disabled={isSubmitting || !isValid || !dirty}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Dropdown.Menu>
    </Dropdown>
  )
}
